import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';
import { CURRENT_USER_MEMBERSHIP_TITLE_QUERY_KEY } from '@/hooks/useCurrentUserMembershipTitle';
import { currentUserProfileQueryKey } from '@/hooks/useCurrentUserProfile';

type ClinicRole = Database['public']['Enums']['clinic_role'];
type MembershipStatus = Database['public']['Enums']['membership_status'];
type ExceptionType = Database['public']['Enums']['member_exception_type'];

export type MembershipRow = Database['public']['Tables']['clinic_memberships']['Row'];
export type ScheduleExceptionRow =
  Database['public']['Tables']['member_schedule_exceptions']['Row'];

export interface ClinicMemberWithUser extends MembershipRow {
  user: {
    id: string;
    name: string | null;
    email: string;
    phone: string | null;
  } | null;
}

export interface WeekDayHours {
  enabled: boolean;
  start: string;
  end: string;
}

export type WorkingHours = {
  monday: WeekDayHours;
  tuesday: WeekDayHours;
  wednesday: WeekDayHours;
  thursday: WeekDayHours;
  friday: WeekDayHours;
  saturday: WeekDayHours;
  sunday: WeekDayHours;
};

export const DEFAULT_WORKING_HOURS: WorkingHours = {
  monday: { enabled: true, start: '09:00', end: '17:00' },
  tuesday: { enabled: true, start: '09:00', end: '17:00' },
  wednesday: { enabled: true, start: '09:00', end: '17:00' },
  thursday: { enabled: true, start: '09:00', end: '17:00' },
  friday: { enabled: true, start: '09:00', end: '17:00' },
  saturday: { enabled: false, start: '09:00', end: '17:00' },
  sunday: { enabled: false, start: '09:00', end: '17:00' },
};

export function readWorkingHours(value: unknown): WorkingHours {
  const merged: WorkingHours = { ...DEFAULT_WORKING_HOURS };
  if (!value || typeof value !== 'object') return merged;
  const src = value as Record<string, unknown>;
  (Object.keys(merged) as Array<keyof WorkingHours>).forEach((day) => {
    const entry = src[day];
    if (entry && typeof entry === 'object') {
      const e = entry as Record<string, unknown>;
      merged[day] = {
        enabled: typeof e.enabled === 'boolean' ? e.enabled : merged[day].enabled,
        start: typeof e.start === 'string' ? e.start : merged[day].start,
        end: typeof e.end === 'string' ? e.end : merged[day].end,
      };
    }
  });
  return merged;
}

export const usersListQueryKey = (clinicId: string | null, includeInactive: boolean) =>
  ['clinic-users', 'list', clinicId, { includeInactive }] as const;

export const usersListForClinicsQueryKey = (clinicIds: string[], includeInactive: boolean) =>
  ['clinic-users', 'list-many', clinicIds, { includeInactive }] as const;

export const pendingInvitationsQueryKey = (clinicId: string | null) =>
  ['clinic-users', 'pending-invitations', clinicId] as const;

export const userDetailQueryKey = (membershipId: string | null) =>
  ['clinic-users', 'detail', membershipId] as const;

export type PendingInvitation = {
  id: string;
  email: string;
  role: ClinicRole;
  custom_role_id: string | null;
  display_name: string | null;
  expires_at: string;
  created_at: string;
};

export function usePendingInvitations(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: pendingInvitationsQueryKey(clinicId ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<PendingInvitation[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('user_invitations')
        .select('id, email, role, custom_role_id, display_name, expires_at, created_at')
        .eq('clinic_id', clinicId)
        .is('accepted_at', null)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as PendingInvitation[];
    },
    staleTime: 30_000,
  });
}

export const userExceptionsQueryKey = (membershipId: string | null) =>
  ['clinic-users', 'exceptions', membershipId] as const;

async function fetchClinicMemberships(
  clinicIds: string[],
  includeInactive: boolean,
): Promise<ClinicMemberWithUser[]> {
  if (clinicIds.length === 0) return [];
  let query = supabase
    .from('clinic_memberships')
    .select('*, user:users!clinic_memberships_user_id_fkey(id, name, email, phone)')
    .in('clinic_id', clinicIds)
    .order('created_at', { ascending: true });

  if (!includeInactive) {
    query = query.eq('status', 'active');
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as unknown as ClinicMemberWithUser[];
}

export function useClinicUsersList(
  clinicId: string | null | undefined,
  options: { includeInactive?: boolean } = {},
) {
  const includeInactive = Boolean(options.includeInactive);
  return useQuery({
    queryKey: usersListQueryKey(clinicId ?? null, includeInactive),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ClinicMemberWithUser[]> => {
      if (!clinicId) return [];
      return fetchClinicMemberships([clinicId], includeInactive);
    },
    staleTime: 30_000,
  });
}

export function useClinicUsersListForClinics(
  clinicIds: string[],
  options: { includeInactive?: boolean } = {},
) {
  const includeInactive = Boolean(options.includeInactive);
  const ids = [...new Set(clinicIds.filter(Boolean))].sort();
  return useQuery({
    queryKey: usersListForClinicsQueryKey(ids, includeInactive),
    enabled: ids.length > 0,
    queryFn: async (): Promise<ClinicMemberWithUser[]> => fetchClinicMemberships(ids, includeInactive),
    staleTime: 30_000,
  });
}

export function useClinicUser(membershipId: string | null | undefined) {
  return useQuery({
    queryKey: userDetailQueryKey(membershipId ?? null),
    enabled: Boolean(membershipId),
    queryFn: async (): Promise<ClinicMemberWithUser | null> => {
      if (!membershipId) return null;
      const { data, error } = await supabase
        .from('clinic_memberships')
        .select('*, user:users!clinic_memberships_user_id_fkey(id, name, email, phone)')
        .eq('id', membershipId)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as ClinicMemberWithUser) ?? null;
    },
    staleTime: 30_000,
  });
}

export function useMemberExceptions(membershipId: string | null | undefined) {
  return useQuery({
    queryKey: userExceptionsQueryKey(membershipId ?? null),
    enabled: Boolean(membershipId),
    queryFn: async (): Promise<ScheduleExceptionRow[]> => {
      if (!membershipId) return [];
      const { data, error } = await supabase
        .from('member_schedule_exceptions')
        .select('*')
        .eq('membership_id', membershipId)
        .order('starts_on', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 30_000,
  });
}

export interface UpdateMemberProfileInput {
  membership_id: string;
  title?: string | null;
  phone?: string | null;
  photo_url?: string | null;
  color?: string;
  bio?: string | null;
  specializations?: string[];
  qualifications?: string[];
  experience?: string | null;
  show_in_booking?: boolean;
  confirm_journal_patient?: boolean;
}

export function useUpdateMemberProfile(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateMemberProfileInput): Promise<MembershipRow> => {
      const { data, error } = await supabase.rpc('update_member_profile', {
        p_membership_id: input.membership_id,
        p_title: input.title ?? undefined,
        p_phone: input.phone ?? undefined,
        // Allow '' to clear photo; omit only when undefined (leave unchanged).
        p_photo_url: input.photo_url === undefined ? undefined : (input.photo_url ?? ''),
        p_color: input.color ?? undefined,
        p_bio: input.bio ?? undefined,
        p_specializations: input.specializations ?? undefined,
        p_qualifications: input.qualifications ?? undefined,
        p_experience: input.experience ?? undefined,
        p_show_in_booking: input.show_in_booking ?? undefined,
        p_confirm_journal_patient: input.confirm_journal_patient ?? undefined,
      });
      if (error) throw error;
      return data as MembershipRow;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['clinic-users', 'list', clinicId ?? null] });
      qc.invalidateQueries({ queryKey: userDetailQueryKey(variables.membership_id) });
      qc.invalidateQueries({ queryKey: [CURRENT_USER_MEMBERSHIP_TITLE_QUERY_KEY] });
      qc.invalidateQueries({ queryKey: ['journal-patient-confirm-membership'] });
    },
  });
}

export function useUpdateMemberUserName(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      membership_id: string;
      name: string;
    }): Promise<{ id: string; name: string | null; email: string }> => {
      const { data, error } = await supabase.rpc('update_member_user_name', {
        p_membership_id: vars.membership_id,
        p_name: vars.name,
      });
      if (error) throw error;
      return data as { id: string; name: string | null; email: string };
    },
    onSuccess: (data, variables) => {
      qc.invalidateQueries({ queryKey: ['clinic-users', 'list', clinicId ?? null] });
      qc.invalidateQueries({ queryKey: userDetailQueryKey(variables.membership_id) });
      qc.invalidateQueries({ queryKey: currentUserProfileQueryKey(data.id) });
    },
  });
}

export function useSetMemberWorkingHours(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      membership_id: string;
      working_hours: WorkingHours;
    }): Promise<MembershipRow> => {
      const { data, error } = await supabase.rpc('set_member_working_hours', {
        p_membership_id: vars.membership_id,
        p_working_hours:
          vars.working_hours as unknown as Database['public']['Tables']['clinic_memberships']['Row']['working_hours'],
      });
      if (error) throw error;
      return data as MembershipRow;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['clinic-users', 'list', clinicId ?? null] });
      qc.invalidateQueries({ queryKey: ['clinic-members', clinicId ?? null] });
      qc.invalidateQueries({ queryKey: userDetailQueryKey(variables.membership_id) });
      qc.invalidateQueries({ queryKey: ['clinic-members'] });
      qc.invalidateQueries({ queryKey: ['public-booking'] });
      qc.invalidateQueries({ queryKey: ['member-schedule-exceptions'] });
    },
  });
}

export function useChangeMemberRole(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      membership_id: string;
      new_role: ClinicRole;
    }): Promise<MembershipRow> => {
      const { data, error } = await supabase.rpc('change_member_role', {
        p_membership_id: vars.membership_id,
        p_new_role: vars.new_role,
      });
      if (error) throw error;
      return data as MembershipRow;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['clinic-users', 'list', clinicId ?? null] });
      qc.invalidateQueries({ queryKey: userDetailQueryKey(variables.membership_id) });
    },
  });
}

export function useRemoveClinicMembership(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (membership_id: string): Promise<MembershipRow> => {
      const { data, error } = await supabase.rpc('remove_clinic_membership', {
        p_membership_id: membership_id,
      });
      if (error) throw error;
      return data as MembershipRow;
    },
    onSuccess: (_data, membership_id) => {
      qc.invalidateQueries({ queryKey: ['clinic-users', 'list', clinicId ?? null] });
      qc.invalidateQueries({ queryKey: userDetailQueryKey(membership_id) });
    },
  });
}

export function useRestoreClinicMembership(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (membership_id: string): Promise<MembershipRow> => {
      const { data, error } = await supabase.rpc('restore_clinic_membership', {
        p_membership_id: membership_id,
      });
      if (error) throw error;
      return data as MembershipRow;
    },
    onSuccess: (_data, membership_id) => {
      qc.invalidateQueries({ queryKey: ['clinic-users', 'list', clinicId ?? null] });
      qc.invalidateQueries({ queryKey: userDetailQueryKey(membership_id) });
    },
  });
}

export interface AddExceptionInput {
  membership_id: string;
  exception_type: ExceptionType;
  starts_on: string;
  ends_on: string;
  starts_time?: string | null;
  ends_time?: string | null;
  reason?: string | null;
}

export function useAddMemberException() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddExceptionInput): Promise<ScheduleExceptionRow> => {
      const { data, error } = await supabase.rpc('add_member_exception', {
        p_membership_id: input.membership_id,
        p_exception_type: input.exception_type,
        p_starts_on: input.starts_on,
        p_ends_on: input.ends_on,
        p_starts_time: input.starts_time ?? undefined,
        p_ends_time: input.ends_time ?? undefined,
        p_reason: input.reason ?? undefined,
      });
      if (error) throw error;
      return data as ScheduleExceptionRow;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: userExceptionsQueryKey(variables.membership_id) });
      qc.invalidateQueries({ queryKey: ['member-schedule-exceptions'] });
      qc.invalidateQueries({ queryKey: ['public-booking'] });
    },
  });
}

export function useUpdateMemberException() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      exception_id: string;
      membership_id: string;
      patch: Partial<Omit<AddExceptionInput, 'membership_id'>> & {
        clear_times?: boolean;
      };
    }): Promise<ScheduleExceptionRow> => {
      const { data, error } = await supabase.rpc('update_member_exception', {
        p_exception_id: vars.exception_id,
        p_exception_type: vars.patch.exception_type ?? undefined,
        p_starts_on: vars.patch.starts_on ?? undefined,
        p_ends_on: vars.patch.ends_on ?? undefined,
        p_starts_time: vars.patch.starts_time ?? undefined,
        p_ends_time: vars.patch.ends_time ?? undefined,
        p_clear_times: vars.patch.clear_times ?? undefined,
        p_reason: vars.patch.reason ?? undefined,
      });
      if (error) throw error;
      return data as ScheduleExceptionRow;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: userExceptionsQueryKey(vars.membership_id) });
      qc.invalidateQueries({ queryKey: ['member-schedule-exceptions'] });
      qc.invalidateQueries({ queryKey: ['public-booking'] });
    },
  });
}

export function useDeleteMemberException() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { exception_id: string; membership_id: string }): Promise<boolean> => {
      const { data, error } = await supabase.rpc('delete_member_exception', {
        p_exception_id: vars.exception_id,
      });
      if (error) throw error;
      return Boolean(data);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: userExceptionsQueryKey(vars.membership_id) });
      qc.invalidateQueries({ queryKey: ['member-schedule-exceptions'] });
      qc.invalidateQueries({ queryKey: ['public-booking'] });
    },
  });
}

export type { ClinicRole, MembershipStatus, ExceptionType };
